import { useState, useEffect, useCallback } from 'react';
import UpdateLivreurForm from "../../components/update/update-livreur-form"; 
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner';
import Loading from '../../cors/Loading'; 

export default function Page() {
  const { token } = useAuth();
  const { id } = useParams(); 
  const [livreur, setLivreur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLivreur = useCallback(async () => {
    if (!id) {
      setError("L'ID du livreur est manquant."); 
      setLoading(false);
      return;
    }

    setLoading(true); 
    setError(null);    
    try {
      if (!token) {
        toast.error("Vous n'êtes pas authentifié. Veuillez vous connecter."); 
        setError("Jeton d'authentification introuvable. Veuillez vous connecter."); 
        setLoading(false);
        return;
      }

      const res = await fetch(`${BASE_URL}/api/livreurs/${id}`, { 
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`, 
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const errorMessage = errorData.message || `Échec de la récupération du livreur : ${res.statusText}`; 
        throw new Error(errorMessage);
      }

      const data = await res.json();
      
      if (data.status && data.data) {
        setLivreur(data.data); 
      
      } else {
        throw new Error(data.message || "Format de données invalide de l'API."); 
      }
    } catch (err) {
      console.error("Erreur lors de la récupération du livreur :", err); 
      setError(err.message || "Une erreur inattendue est survenue lors de la récupération du livreur."); 
      toast.error(`Échec du chargement du livreur.`); 
    } finally {
      setLoading(false);
    } 
  }, [id, token]); 

  useEffect(() => {
    fetchLivreur();
  }, [fetchLivreur]);

  if (loading) {
    return (
    <div className="max-w-5xl mx-auto min-h-screen py-8 px-4 animate-pulse">
      <div className="mx-auto">
        {/* Header Skeleton */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-6 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center space-x-4">
                {/* Logo Skeleton */}
                <div className="w-16 h-16 md:w-32 md:h-32 bg-gray-300 rounded-full border-4 border-white shadow-lg"></div>
                <div className="space-y-2">
                  <div className="h-6 bg-gray-200/50 rounded w-48"></div>
                  <div className="h-4 bg-gray-200/50 rounded w-32"></div>
                  <div className="h-3 bg-gray-200/50 rounded w-40"></div>
                </div>
              </div>
              <div className="mt-4 md:mt-0">
                <div className="bg-white bg-opacity-2 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                  <div className="h-8 bg-gray-200/50 rounded w-16 mx-auto"></div>
                  <div className="h-3 bg-gray-200/50 rounded w-20 mx-auto mt-2"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Stats Skeleton */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="text-center">
                  <div className="h-8 bg-gray-200 rounded w-24 mx-auto"></div>
                  <div className="h-4 bg-gray-200 rounded w-28 mx-auto mt-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-20 mx-auto mt-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Profile Information Skeleton */}
        <div className="bg-white rounded-2xl p-8 max-xl:p-5 shadow-sm gap-8">
          {/* Edit Button Skeleton */}
          <div className="flex justify-end py-5">
            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
          </div>
          
          {/* Form Grid Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Personal Information Skeleton */}
            <div className="max-xl:px-4 p-8">
              <div className="flex items-center mb-6">
                <div className="bg-gray-200 p-3 rounded-lg mr-4"></div>
                <div className="h-6 bg-gray-200 rounded w-48"></div>
              </div>

              <div className="space-y-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i}>
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-12 bg-gray-100 rounded-2xl"></div>
                  </div>
                ))}
                {/* Logo Upload Skeleton */}
                <div>
                  <div className="h-4 bg-gray-200 rounded w-40 mb-2"></div>
                  <div className="h-12 bg-gray-100 rounded-xl"></div>
                </div>
              </div>
            </div>

            {/* Business Information Skeleton */}
            <div className="p-8">
              <div className="flex items-center mb-6">
                <div className="bg-gray-200 p-3 rounded-lg mr-4"></div>
                <div className="h-6 bg-gray-200 rounded w-48"></div>
              </div>

              <div className="space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i}>
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-12 bg-gray-100 rounded-2xl"></div>
                  </div>
                ))}
                {/* Bank Select Skeleton */}
                <div>
                  <div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
                  <div className="h-12 bg-gray-100 rounded-2xl"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Password Section Skeleton */}
        <div className="bg-white rounded-2xl p-8 max-xl:p-5 shadow-sm gap-8 mt-8">
          <div className="flex items-center mb-6">
            <div className="bg-gray-200 p-3 rounded-lg mr-4"></div>
            <div className="h-6 bg-gray-200 rounded w-40"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i}>
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-12 bg-gray-100 rounded-2xl"></div>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-8">
            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
          </div>
        </div>
      </div>
    </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <h2 className="text-2xl font-bold text-red-700 mb-4">Erreur lors du chargement du livreur</h2> 
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={fetchLivreur} 
            className="px-6 py-3 bg-[var(--color-primary-500)] text-white font-semibold rounded-lg hover:bg-[var(--color-primary-400)] transition-colors"
          >
            Réessayer 
          </button>
        </div>
      </div>
    );
  }

  if (!livreur) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-xl font-bold text-gray-700 mb-4">Livreur Introuvable</h2> 
          <p className="text-gray-600 text-xs">Le livreur avec l'ID "{id}" n'a pas pu être chargé ou n'existe pas.</p> 
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="container mx-auto px-4">
        {/* Form Container */}
        <div className="max-w-5xl mx-auto">
          <UpdateLivreurForm livreur={livreur} />
        </div>
      </div>
    </div>
  );
}
